import { Box, Button, TextField, Typography } from "@mui/material";
import { LoadingComponent } from "@gui/widgets/LoadingComponent";
import { AsyncStatus } from "@gui/widgets/A11y";
import { PasswordResetProps } from "./passwordreset.types";
import { usePasswordResetViewModel } from "./passwordreset.vm";
import "./passwordreset.css";

export function PasswordReset(props: PasswordResetProps) {
	const vm = usePasswordResetViewModel(props);

	// Render password update form (with valid token)
	if (vm.isTokenValid) {
		return (
			<Box
				maxWidth="md"
				margin="auto"
				display="flex"
				flexDirection="column"
				component="form"
				noValidate
				className="password-reset-form"
				onSubmit={vm.handlePasswordUpdate}
			>
				{vm.loading && (
					<Box display="flex" justifyContent="center" alignItems="center" aria-busy="true">
						<AsyncStatus state="loading" loadingText="Updating your password…" />
						<LoadingComponent />
					</Box>
				)}
				<TextField
					variant="outlined"
					margin="normal"
					error={!!vm.passwordError}
					helperText={vm.passwordError}
					required
					disabled={vm.loading}
					fullWidth
					name="password"
					label="New Password"
					type="password"
					id="password"
					autoComplete="current-password"
					onChange={vm.handlePasswordChange}
					onBlur={vm.handlePasswordChange}
					data-sentry-component="New Password Field"
				/>
				<TextField
					variant="outlined"
					margin="normal"
					error={!!vm.confirmPasswordError}
					helperText={vm.confirmPasswordError}
					required
					disabled={vm.loading}
					fullWidth
					name="Confirm password"
					label="Confirm Password"
					type="password"
					id="Confirm password"
					autoComplete="confirm-password"
					onChange={vm.handleConfirmPasswordChange}
					onBlur={vm.handleConfirmPasswordChange}
					data-sentry-component="Confirm Password Field"
				/>
				{/* This branch's error genuinely populates — `handlePasswordUpdate` sets it for the
				    expired-token case — so it is a real live region. `color="textSecondary"` renders it
				    in body grey; that is pre-existing styling and left alone here, but it does mean a
				    failure does not read as an error visually. Flagged in #661 rather than changed. */}
				{vm.error && (
					<Typography variant="body2" color="textSecondary" align="center" className="error-message" role="alert">
						{vm.error}
					</Typography>
				)}
				<Typography className="instruction-text">Please enter your new password.</Typography>
				<Button
					type="submit"
					fullWidth
					variant="contained"
					className="submit-button"
					data-sentry-component="Submit Button"
				>
					Update Password
				</Button>
			</Box>
		);
	} else {
		// Render password reset request form (no token)
		return (
			<Box
				maxWidth="md"
				margin="auto"
				display="flex"
				flexDirection="column"
				component="form"
				noValidate
				className="password-reset-form"
				onSubmit={vm.handlePasswordReset}
			>
				{vm.loading && (
					<Box display="flex" justifyContent="center" alignItems="center" aria-busy="true">
						<AsyncStatus state="loading" loadingText="Sending your reset link…" />
						<LoadingComponent />
					</Box>
				)}
				<TextField
					margin="normal"
					required
					id="email"
					label="Email Address"
					name="email"
					autoComplete="email"
					autoFocus
					error={!!vm.emailError}
					helperText={vm.emailError}
					onChange={vm.handleEmailChange}
					onBlur={vm.handleEmailChange}
					data-sentry-component="Email Field"
				/>
				{/* No error element in this branch on purpose: `handlePasswordReset` routes every failure
				    through `Oops.handleError` (a toast) and never calls `setError` with a message, so an
				    element bound to `vm.error` here would be permanently empty — the same dead shape
				    removed from Login. `vm.error` is shared with the update branch above, which does
				    populate it; rendering it here would surface that branch's message on this form. */}
				<Typography className="instruction-text">Please enter the email associated with your Rogo Account.</Typography>
				<Button
					type="submit"
					fullWidth
					variant="contained"
					className="submit-button"
					data-sentry-component="Submit Button"
				>
					Reset Password
				</Button>
			</Box>
		);
	}
}
